import React from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "../api";
import { notifyWorkspaceChanged } from "../workspaceSync";

type PublicPollQuestion = {
  node_id: string;
  label: string;
  prompt: string;
};

type PublicPoll = {
  enterprise_id: number;
  invite_token: string;
  title: string;
  description: string;
  is_anonymous: boolean;
  questions: PublicPollQuestion[];
  invite: {
    recipient_name: string;
    completed_at?: string | null;
  };
};

const isValidRating = (value: string) => {
  const parsed = Number(value.trim());
  return value.trim().length > 0 && !Number.isNaN(parsed) && parsed >= 1 && parsed <= 10;
};

export default function PollResponsePage() {
  const params = useParams();
  const location = useLocation();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const enterpriseId = params.enterpriseId ?? searchParams.get("enterpriseId") ?? undefined;
  const inviteToken = params.inviteToken ?? searchParams.get("inviteToken") ?? undefined;
  const [poll, setPoll] = React.useState<PublicPoll | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitted, setSubmitted] = React.useState(false);
  const [respondentLabel, setRespondentLabel] = React.useState("");
  const [ratingValues, setRatingValues] = React.useState<Record<string, string>>({});
  const [commentValues, setCommentValues] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const loadPoll = async () => {
      if (!enterpriseId || !inviteToken) {
        setError("This poll link is missing information.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/public/polls/respond/${enterpriseId}/${inviteToken}`);
        const nextPoll = response.data as PublicPoll;
        setPoll(nextPoll);
        setSubmitted(Boolean(nextPoll.invite.completed_at));
      } catch (loadError) {
        console.error("Failed to load poll", loadError);
        setError("We could not load this poll.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadPoll();
  }, [enterpriseId, inviteToken]);

  const canSubmit =
    Boolean(poll) &&
    !isSubmitting &&
    (poll?.questions.length ?? 0) > 0 &&
    (poll?.questions ?? []).every((question) => isValidRating(ratingValues[question.node_id] ?? ""));

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!enterpriseId || !inviteToken || !poll) {
      return;
    }

    if (!canSubmit) {
      setError("Please answer every KPI with a rating from 1 to 10 before submitting.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await api.post(`/public/polls/respond/${enterpriseId}/${inviteToken}`, {
        respondent_label: respondentLabel.trim(),
        answers: poll.questions.map((question) => ({
          node_id: question.node_id,
          value: Number((ratingValues[question.node_id] ?? "").trim()),
          comment: (commentValues[question.node_id] ?? "").trim(),
        })),
      });
      notifyWorkspaceChanged(Number(enterpriseId), "poll-response");
      setSubmitted(true);
    } catch (submitError) {
      console.error("Failed to submit poll response", submitError);
      setError("Your response could not be submitted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div style={{ padding: 32, fontSize: 16 }}>Loading poll...</div>;
  }

  if (error && !poll) {
    return <div style={{ padding: 32, fontSize: 16, color: "#b91c1c" }}>{error}</div>;
  }

  if (!poll) {
    return <div style={{ padding: 32, fontSize: 16, color: "#b91c1c" }}>This poll link is no longer valid.</div>;
  }

  const primaryQuestion = poll.questions[0];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f5f1e8 0%, #f9fafb 45%, #ffffff 100%)",
        padding: "32px 24px",
        color: "#111827",
      }}
    >
      <div style={{ maxWidth: 760, margin: "0 auto", display: "grid", gap: 18 }}>
        <div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Poll Response
            </div>
            <h1 style={{ margin: "8px 0 0", fontSize: 34, lineHeight: 1.05 }}>{poll.title}</h1>
            <div style={{ marginTop: 10, fontSize: 15, color: "#4b5563" }}>
              KPI: {primaryQuestion?.label || "Untitled KPI"}
            </div>
          </div>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 20, padding: 22, display: "grid", gap: 16 }}>
          <div style={{ fontSize: 15, color: "#374151", lineHeight: 1.6 }}>
            {poll.description || "Please complete the poll response below."}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {poll.is_anonymous
              ? "This poll is set to anonymous."
              : `Your response will be tracked for ${poll.invite.recipient_name || "this invitation"}.`}
          </div>

          {submitted ? (
            <div style={{ border: "1px solid #bbf7d0", background: "#f0fdf4", borderRadius: 16, padding: 18, color: "#166534", fontWeight: 700 }}>
              Thanks. Your response has been recorded.
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ display: "grid", gap: 16 }}>
              {!poll.is_anonymous ? (
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                    Responding As
                  </span>
                  <input
                    value={respondentLabel || poll.invite.recipient_name}
                    onChange={(event) => setRespondentLabel(event.target.value)}
                    placeholder="Your name"
                    style={{ border: "1px solid #d1d5db", borderRadius: 12, padding: "12px 14px", fontSize: 15 }}
                  />
                </label>
              ) : null}

              {poll.questions.map((question) => (
                <div
                  key={question.node_id}
                  style={{
                    display: "grid",
                    gap: 12,
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    padding: 16,
                    background: "#f9fafb",
                  }}
                >
                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                      {poll.questions.length > 1 ? `KPI Question: ${question.label}` : "Question"}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, whiteSpace: "pre-wrap" }}>{question.prompt}</div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                      Please rate this on a 1-10 scale, where 1 is lowest and 10 is highest.
                    </div>
                  </div>

                  <input
                    type="number"
                    value={ratingValues[question.node_id] ?? ""}
                    onChange={(event) =>
                      setRatingValues((current) => ({
                        ...current,
                        [question.node_id]: event.target.value,
                      }))
                    }
                    min={1}
                    max={10}
                    step={1}
                    placeholder="Enter a rating from 1 to 10"
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 12,
                      padding: "12px 14px",
                      fontSize: 15,
                      background: "#fff",
                    }}
                  />

                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                      Comments
                    </span>
                    <textarea
                      value={commentValues[question.node_id] ?? ""}
                      onChange={(event) =>
                        setCommentValues((current) => ({
                          ...current,
                          [question.node_id]: event.target.value,
                        }))
                      }
                      rows={4}
                      placeholder="Share any evidence, examples, or observations behind your rating"
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 12,
                        padding: "12px 14px",
                        fontSize: 15,
                        resize: "vertical",
                        background: "#fff",
                      }}
                    />
                  </label>
                </div>
              ))}

              {error ? <div style={{ color: "#b91c1c", fontSize: 14, fontWeight: 700 }}>{error}</div> : null}

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  border: "none",
                  borderRadius: 12,
                  padding: "12px 16px",
                  background: canSubmit ? "#111827" : "#9ca3af",
                  color: "#fff",
                  fontWeight: 800,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit Response"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
